/**
 * @fileoverview Component for displaying a login form.
 * This component includes a form for logging in and displays a message if the login is incorrect or the user is logged out due to security reasons.
 */

import { InformationCircleIcon, XCircleIcon } from '@heroicons/react/20/solid'
import { useState } from 'react'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'
import NovoicDashboardLogo from '../../assets/novoic-dashboard-logo.svg'
import { useUserAPI } from '../../hooks/useUserAPI'
import { useLoginMutation } from '../../services/users'
import { TextInput } from '../form/TextInput'

/**
 * @function Login
 * @description Component for displaying a login form.
 * @returns {JSX.Element} - A React component that renders the login form.
 */
export default function Login() {
    const navigate = useNavigate()
    const userAPI = useUserAPI()
    const [logIn, { isLoading }] = useLoginMutation()
    const [error, setError] = useState<Error>()
    const [params] = useSearchParams()
    const isTimedOut = params.get('timeout') === 'true'
    const isLoggedOutDueToSecurityReasons = params.get('security') === 'true'

    if (userAPI.isLoggedIn()) {
        setTimeout(() => navigate('/app'))
    }

    async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()

        const formData = new FormData(e.currentTarget)
        const payload: Record<string, string> = {}

        for (const [key, value] of formData.entries()) {
            payload[key] = value as string
        }

        try {
            const data = await logIn(payload as { email: string; password: string }).unwrap()
            setError(undefined)
            userAPI.logIn(data.user, data.intercom, data.token)
            const returnURL = params.get('return') || '/app'
            window.location.href = returnURL
        } catch (e: unknown) {
            console.log(e)
            setError(e as Error)
        }
    }

    return (
        <div className="nv-grid min-h-screen">
            <div className="md-container max-h-full items-center content-center justify-center col-start-1 md:col-start-2 lg:col-start-5">
                <div className="w-full max-w-md space-y-8">
                    <div className="flex flex-col items-center gap-y-4">
                        <NovoicDashboardLogo className="w-56" />
                        <h2 className="text-center text-3xl font-bold font-serif text-gray-900">
                            Log in to your account
                        </h2>
                    </div>
                    <form className="relative space-y-6" action="#" onSubmit={onSubmit}>
                        <input type="hidden" name="remember" defaultValue="true" />
                        <div className="flex flex-col gap-y-4 rounded-md shadow-sm">
                            <div>
                                <label
                                    htmlFor="login"
                                    className="font-medium text-sm text-gray-700 pb-1"
                                >
                                    Email
                                </label>
                                <TextInput
                                    id="login"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    required
                                    className="rounded-md px-3 py-2"
                                    placeholder="Email"
                                    validationError="Please, fill in your email"
                                    error={error}
                                />
                            </div>
                            <div>
                                <label
                                    htmlFor="password"
                                    className="font-medium text-sm text-gray-700 pb-1"
                                >
                                    Password
                                </label>
                                <TextInput
                                    id="password"
                                    name="password"
                                    type="password"
                                    autoComplete="current-password"
                                    required
                                    className="rounded-md px-3 py-2"
                                    placeholder="Password"
                                    validationError="Please, fill in your password"
                                    error={error}
                                />
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-base font-medium text-white disabled:opacity-75 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                            >
                                Log In
                            </button>
                        </div>

                        {isTimedOut && (
                            <div className="rounded-md bg-indigo-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <InformationCircleIcon
                                            className="h-5 w-5 text-indigo-400"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="ml-3 flex-1 md:flex md:justify-between">
                                        <p className="text-sm text-blue-800 font-medium">
                                            You were automatically logged out after 15 minutes of
                                            inactivity for security reasons.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {isLoggedOutDueToSecurityReasons && (
                            <div className="rounded-md bg-indigo-50 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <InformationCircleIcon
                                            className="h-5 w-5 text-indigo-400"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="ml-3 flex-1 md:flex md:justify-between">
                                        <p className="text-sm text-blue-800 font-medium">
                                            You were automatically logged out due to security
                                            reasons.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="w-full rounded-md bg-red-50 p-4 transition-opacity">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <XCircleIcon
                                            className="h-5 w-5 text-red-400"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-red-800">
                                            Incorrect login or password
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>

                    <div className="flex justify-center items-center">
                        <NavLink
                            to="/password/request"
                            className="inline-flex transition-all rounded-md text-indigo-600 hover:bg-indigo-50"
                        >
                            <div className="w-fit">
                                <span className="block text-sm font-medium px-2 py-1">
                                    Forgot password?
                                </span>
                            </div>
                        </NavLink>
                    </div>
                </div>
            </div>
        </div>
    )
}
